import { Button, useTranslation } from '@hbs-us/ui';
import { useState } from 'react';

import { ConfirmDialog } from '../_money/ConfirmDialog.js';
import type { DispatchAssignmentStatus, DispatchCategoryDto } from './contractsMirror.js';
import { useCreateCategoryMutation, useDeleteCategoryMutation } from './useAssignMutation.js';

/**
 * The dispatch management panel (Plan 22-23 — now a real manage UI, not a read-only list). It
 * surfaces the two legacy management concepts (`dispatch_categories` + the status enum) AND lets a
 * dispatcher create a category (name + a swatch from the server-provided palette) and delete one
 * (behind a destructive confirm). Both write through the tenant-scoped, jobs.edit-gated category
 * endpoints (SEC-11).
 *
 * BRAND-06: the swatch colors come from `colorPalette` / `category.color` — server DATA rendered via
 * an inline CSS custom property, never a hardcoded hex in source. Category names render as TEXT
 * beside every swatch (never color alone — A11Y-01).
 */

const STATUSES: readonly DispatchAssignmentStatus[] = [
  'acknowledged',
  'enroute',
  'onsite',
  'completed',
];

export function DispatchManageView({
  categories,
  colorPalette,
}: {
  categories: DispatchCategoryDto[];
  colorPalette: string[];
}) {
  const { t } = useTranslation();
  const createMutation = useCreateCategoryMutation();
  const deleteMutation = useDeleteCategoryMutation();

  const [name, setName] = useState('');
  const [color, setColor] = useState<string>(colorPalette[0] ?? '');
  const [pendingDelete, setPendingDelete] = useState<DispatchCategoryDto | null>(null);

  const effectiveColor = colorPalette.includes(color) ? color : (colorPalette[0] ?? '');
  const canCreate = name.trim() !== '' && name.trim().length <= 50 && effectiveColor !== '';

  const handleCreate = () => {
    if (!canCreate) return;
    createMutation.mutate(
      { name: name.trim(), color: effectiveColor },
      {
        onSuccess: () => {
          setName('');
          setColor(colorPalette[0] ?? '');
        },
      },
    );
  };

  const handleConfirmDelete = () => {
    if (!pendingDelete) return;
    deleteMutation.mutate({ id: pendingDelete.id }, { onSuccess: () => setPendingDelete(null) });
  };

  return (
    <section
      aria-label={t('dispatch:manage.heading')}
      className="grid gap-4 rounded-md border border-border bg-card p-4 sm:grid-cols-2"
    >
      <div>
        <h2 className="font-display font-semibold text-foreground text-sm">
          {t('dispatch:manage.categories')}
        </h2>

        {categories.length === 0 ? (
          <p className="mt-2 text-muted-foreground text-xs">{t('dispatch:manage.noCategories')}</p>
        ) : (
          <ul className="mt-2 flex flex-col gap-1.5">
            {categories.map((cat) => (
              <li
                key={cat.id}
                className="flex items-center justify-between gap-2 text-foreground text-sm"
              >
                <span className="flex items-center gap-2">
                  <span
                    aria-hidden="true"
                    className="inline-block h-3 w-3 rounded-full border border-border"
                    style={{ backgroundColor: cat.color }}
                  />
                  <span>{cat.name}</span>
                </span>
                <button
                  type="button"
                  onClick={() => setPendingDelete(cat)}
                  className="inline-flex h-7 items-center rounded-sm bg-destructive/10 px-2 font-medium text-[0.7rem] text-destructive transition-colors hover:bg-destructive/20 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                >
                  {t('dispatch:manage.deleteCategory')}
                </button>
              </li>
            ))}
          </ul>
        )}

        {/* Create-category form — name + a swatch from the server palette (no hex in source). */}
        <div className="mt-4 flex flex-col gap-2">
          <label className="block text-foreground text-xs" htmlFor="dispatch-cat-name">
            {t('dispatch:manage.newCategoryName')}
            <input
              id="dispatch-cat-name"
              type="text"
              maxLength={50}
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="mt-1 h-10 w-full rounded-md border border-border bg-background px-3 text-foreground text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            />
          </label>

          <fieldset className="flex flex-col gap-1">
            <legend className="text-foreground text-xs">
              {t('dispatch:manage.newCategoryColor')}
            </legend>
            <div className="flex flex-wrap gap-1.5">
              {colorPalette.map((paletteColor) => {
                const selected = paletteColor === effectiveColor;
                return (
                  <button
                    key={paletteColor}
                    type="button"
                    aria-pressed={selected}
                    aria-label={t('dispatch:manage.selectColor', { color: paletteColor })}
                    onClick={() => setColor(paletteColor)}
                    className={`h-6 w-6 rounded-full border transition-transform focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${
                      selected ? 'scale-110 border-foreground' : 'border-border'
                    }`}
                    style={{ backgroundColor: paletteColor }}
                  />
                );
              })}
            </div>
          </fieldset>

          <div>
            <Button
              onClick={handleCreate}
              disabled={!canCreate || createMutation.isPending}
              className="h-9"
            >
              {t('dispatch:manage.addCategory')}
            </Button>
          </div>
        </div>
      </div>

      <div>
        <h2 className="font-display font-semibold text-foreground text-sm">
          {t('dispatch:manage.statuses')}
        </h2>
        <ul className="mt-2 flex flex-col gap-1.5">
          {STATUSES.map((status) => (
            <li key={status} className="text-foreground text-sm">
              {t(`dispatch:status.${status}`)}
            </li>
          ))}
        </ul>
      </div>

      <ConfirmDialog
        open={pendingDelete !== null}
        heading={t('dispatch:manage.deleteHeading')}
        body={t('dispatch:manage.deleteBody', { name: pendingDelete?.name ?? '' })}
        confirmLabel={t('dispatch:manage.deleteConfirm')}
        cancelLabel={t('dispatch:fallback.cancel')}
        confirmPending={deleteMutation.isPending}
        onConfirm={handleConfirmDelete}
        onCancel={() => setPendingDelete(null)}
      />
    </section>
  );
}
